import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { X, Download, ExternalLink, ChevronLeft, ChevronRight, FileText, Image, File } from 'lucide-react';

const FileViewer = ({ isOpen, onClose, file, files = [], onNavigate }) => {
  if (!file) return null;

  const currentIndex = files.findIndex(f => f.id === file.id);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < files.length - 1;

  const handlePrev = () => {
    if (hasPrev && onNavigate) {
      onNavigate(files[currentIndex - 1]);
    }
  };

  const handleNext = () => {
    if (hasNext && onNavigate) {
      onNavigate(files[currentIndex + 1]);
    }
  };

  const isImage = file.resource_type === 'image' || 
    ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(file.format?.toLowerCase());
  
  const isPDF = file.format?.toLowerCase() === 'pdf';

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = file.url;
    link.download = file.filename || 'download';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[95vh] p-0 overflow-hidden">
        <DialogHeader className="p-4 border-b flex flex-row items-center justify-between">
          <div className="flex items-center gap-3">
            {isImage ? <Image className="w-5 h-5 text-[#1F3C4F]" /> : 
             isPDF ? <FileText className="w-5 h-5 text-red-500" /> : 
             <File className="w-5 h-5 text-slate-500" />}
            <DialogTitle className="font-heading text-lg">{file.filename}</DialogTitle>
            {files.length > 1 && (
              <span className="text-sm text-slate-500">
                ({currentIndex + 1} of {files.length})
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleDownload}>
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
            <Button variant="outline" size="sm" onClick={() => window.open(file.url, '_blank')}>
              <ExternalLink className="w-4 h-4 mr-2" />
              Open
            </Button>
          </div>
        </DialogHeader>
        
        <div className="relative bg-slate-900 flex items-center justify-center" style={{ height: 'calc(95vh - 80px)' }}>
          {/* Navigation arrows */}
          {files.length > 1 && (
            <>
              <button
                onClick={handlePrev}
                disabled={!hasPrev}
                className={`absolute left-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors ${!hasPrev ? 'opacity-30 cursor-not-allowed' : ''}`}
              >
                <ChevronLeft className="w-6 h-6 text-white" />
              </button>
              <button
                onClick={handleNext}
                disabled={!hasNext}
                className={`absolute right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors ${!hasNext ? 'opacity-30 cursor-not-allowed' : ''}`}
              >
                <ChevronRight className="w-6 h-6 text-white" />
              </button>
            </>
          )}

          {/* Content */}
          {isImage ? (
            <img 
              src={file.url} 
              alt={file.filename}
              className="max-w-full max-h-full object-contain"
            />
          ) : isPDF ? (
            <iframe
              src={`${file.url}#toolbar=1&navpanes=0`}
              className="w-full h-full bg-white"
              title={file.filename}
            />
          ) : (
            <div className="text-center text-white p-8">
              <File className="w-24 h-24 mx-auto text-slate-400 mb-4" />
              <p className="text-xl font-heading mb-2">{file.filename}</p>
              <p className="text-slate-400 mb-6">Preview not available for this file type</p>
              <Button onClick={handleDownload} className="bg-[#1F3C4F] hover:bg-[#152a38]">
                <Download className="w-4 h-4 mr-2" />
                Download File
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FileViewer;
